import { useEffect, type ReactNode } from 'react'
import type { SedeConfig } from '../sedi/types'
import { LEGAL_PATHS } from '../legal/paths'
import { PAGE } from './strings'
import '@shared/contact-form/rivisto.css'
import './reviews.css'

/**
 * The frame of the review pages: the site's own root class (so its paper,
 * ink, display face, dark theme and --rf-* palette apply), a slim brand
 * header like the legal pages', and a footer with the privacy link.
 * Client-rendered only (App.tsx matches these paths before the site renders).
 */
export function siteRootClass(slug: string): string {
  if (slug === 'verde-vercelli-2') return 'vc2-root'
  if (slug === 'verde-valsesia-2') return 'vv2-root'
  return ''
}

/** The sede's own privacy page: the API's link first, else the legal section's. */
export function privacyHref(sede: SedeConfig, fromApi?: string): string {
  return fromApi || (sede.legalPages ? LEGAL_PATHS.privacy.it : '/privacy/')
}

/**
 * These URLs fall back to the home's prerendered index.html, whose head is
 * the home's: retitle, drop its canonical/hreflang and keep the page out of
 * the index (and out of the sitemap, which only lists ROUTES).
 */
export function useNoindexHead(title: string) {
  useEffect(() => {
    document.title = title
    document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove())
    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
    if (!robots) {
      robots = document.createElement('meta')
      robots.name = 'robots'
      document.head.appendChild(robots)
    }
    robots.content = 'noindex, nofollow'
  }, [title])
}

export function ReviewChrome({
  sede,
  privacyUrl,
  footerAction,
  children,
}: {
  sede: SedeConfig
  privacyUrl?: string
  footerAction?: ReactNode
  children: ReactNode
}) {
  const header = sede.legalPages?.header
  const home = sede.legalPages?.homePath.it ?? '/'
  return (
    <div className={`${siteRootClass(sede.slug)} rv-page`.trim()}>
      <header className="rv-header">
        <div className="rv-wrap">
          <a className="rv-brand" href={home} aria-label={`${sede.brand.name} — ${PAGE.backHome}`}>
            {header?.logoSrc && <img src={header.logoSrc} alt="" width={36} height={36} />}
            <span>
              <span className="rv-brand-name">{sede.brand.name}</span>
              {header?.tagline && <span className="rv-brand-tag">{header.tagline}</span>}
            </span>
          </a>
        </div>
      </header>
      <main id="main" className="rv-main">
        <div className="rv-wrap">{children}</div>
      </main>
      <footer className="rv-footer">
        <div className="rv-wrap">
          <a href={privacyHref(sede, privacyUrl)}>{PAGE.privacy}</a>
          {footerAction}
          <span className="rv-legal">{sede.legal?.legalName ?? sede.brand.name}</span>
        </div>
      </footer>
    </div>
  )
}
